import React from 'react';
import { ChevronRight } from 'lucide-react';
import { LifecycleBadge, EventLegend } from '../components/LifecycleBadge';

export const drillStaffCalendarDay = (ctx, dateString, isFuture = false) => {
  ctx.setStaffCustomRange({
    startDate: dateString,
    endDate: dateString,
    startTime: ctx.businessStartTime,
    endTime: ctx.businessEndTime,
  });
  if (isFuture && ctx.activeTab === 'advances') {
    ctx.setActiveTab('salary_schedule');
    ctx.setSalaryView('table');
  } else if (ctx.activeTab === 'attendance') ctx.setAttView('table');
  else if (ctx.activeTab === 'leave') ctx.setLeaveView('table');
  else if (ctx.activeTab === 'roster') ctx.setRosterView('table');
  else if (ctx.activeTab === 'deductions') ctx.setDeductionsView('table');
  else ctx.setAdvancesView('table');
};

/**
 * Month-grid and year-grid calendar for the Staff Management record tabs
 * (attendance / leave / roster / advances / deductions).
 *
 * Lifted VERBATIM out of StaffManagement.js (script-copied, not retyped). All
 * data now arrives pre-derived from utils/staffRecordsEngine, so this is purely
 * presentational; everything it references comes in through the `ctx` bag.
 */
export function renderStaffRecordsCalendar(ctx) {
  const activeTab = ctx.activeTab;
  const advances = ctx.advances;
  const attendance = ctx.attendance;
  const businessEndTime = ctx.businessEndTime;
  const businessStartTime = ctx.businessStartTime;
  const calMonthYM = ctx.calMonthYM;
  const calendarData = ctx.calendarData;
  const changeCalMonth = ctx.changeCalMonth;
  const datePreset = ctx.datePreset;
  const drillToMonth = ctx.drillToMonth;
  const getHolidayForDate = ctx.getHolidayForDate;
  const setActiveTab = ctx.setActiveTab;
  const setAdvancesView = ctx.setAdvancesView;
  const setAttView = ctx.setAttView;
  const setDatePreset = ctx.setDatePreset;
  const setDeductionsView = ctx.setDeductionsView;
  const setEndDate = ctx.setEndDate;
  const setEndTime = ctx.setEndTime;
  const setLeaveView = ctx.setLeaveView;
  const setRosterView = ctx.setRosterView;
  const setSalaryView = ctx.setSalaryView;
  const setStartDate = ctx.setStartDate;
  const setStartTime = ctx.setStartTime;
  const startDate = ctx.startDate;
  const todayStr = ctx.todayStr;
  const yearCalData = ctx.yearCalData;

              const MO_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
              const MO_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

              /* ── Year Grid ── */
              if (datePreset === 'year' && yearCalData) {
                const calYr = new Date(startDate || new Date()).getFullYear();
                return (
                  <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
                    <h3 className="font-semibold text-gray-800 mb-4">{calYr} — Year Overview</h3>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                      {MO_SHORT.map((mName, mi) => {
                        const ms = `${calYr}-${String(mi+1).padStart(2,'0')}`;
                        const agg = yearCalData[ms] || {};
                        const isCurrentOrPast = ms <= todayStr.substring(0,7);
                        let bg = 'bg-gray-50 border-gray-200', txt = 'text-gray-400';
                        if (isCurrentOrPast) {
                          if (activeTab === 'attendance') { bg = (agg.present || 0) > 0 ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'; txt = 'text-green-800'; }
                          else if (activeTab === 'leave') { bg = (agg.count || 0) > 0 ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'; txt = 'text-blue-800'; }
                          else if (activeTab === 'roster') { bg = (agg.count || 0) > 0 ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'; txt = 'text-green-800'; }
                          else if (activeTab === 'advances') { bg = (agg.count || 0) > 0 ? 'bg-gray-100 border-gray-800' : 'bg-gray-50 border-gray-200'; txt = 'text-gray-950'; }
                          else if (activeTab === 'deductions') { bg = (agg.count || 0) > 0 ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'; txt = 'text-red-800'; }
                        }
                        return (
                          <div key={ms} onClick={() => isCurrentOrPast && drillToMonth(ms)}
                            className={`${bg} border rounded-xl p-3 ${isCurrentOrPast ? 'cursor-pointer hover:shadow-md transition' : 'opacity-40'}`}>
                            <div className={`font-bold text-sm ${txt}`}>{mName}</div>
                            {isCurrentOrPast && (
                              <div className="mt-1 space-y-0.5">
                                {activeTab === 'attendance' && <>
                                  {(agg.present || 0) > 0 && <div className="text-xs text-green-700">✓ {agg.present} punch-ins</div>}
                                  {(agg.leaves || 0) > 0 && <div className="text-xs text-blue-600">🏖 {agg.leaves} leaves</div>}
                                </>}
                                {activeTab === 'leave' && <div className="text-xs text-blue-700">{agg.count || 0} leaves</div>}
                                {activeTab === 'roster' && <div className="text-xs text-green-700">{agg.count || 0} shifts</div>}
                                {activeTab === 'advances' && <>
                                  {(agg.count || 0) > 0 && <div className="text-xs text-gray-950">{agg.count} entries</div>}
                                  {(agg.total || 0) > 0 && <div className="text-xs text-gray-950 font-semibold">Rs.{(agg.total/1000).toFixed(1)}K</div>}
                                </>}
                                {activeTab === 'deductions' && <>
                                  {(agg.count || 0) > 0 && <div className="text-xs text-red-700">{agg.count} deductions</div>}
                                  {(agg.total || 0) > 0 && <div className="text-xs text-red-600 font-semibold">Rs.{(agg.total/1000).toFixed(1)}K</div>}
                                </>}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              }

              const [yr, mo] = calMonthYM.split('-').map(Number);
              const daysInMonth = new Date(yr, mo, 0).getDate();
              const firstDow = new Date(yr, mo - 1, 1).getDay();
              const firstMon = firstDow === 0 ? 6 : firstDow - 1;
              const cells = [];
              for (let i = 0; i < firstMon; i++) cells.push(null);
              for (let d = 1; d <= daysInMonth; d++) cells.push(d);
              return (
                <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
                  <div className="flex items-center justify-between mb-4">
                    <button onClick={() => changeCalMonth(-1)} className="p-2 hover:bg-gray-100 rounded-lg transition"><ChevronRight size={18} className="rotate-180" /></button>
                    <h3 className="font-semibold text-gray-800 text-base">{MO_NAMES[mo-1]} {yr}</h3>
                    <button onClick={() => changeCalMonth(1)} className="p-2 hover:bg-gray-100 rounded-lg transition"><ChevronRight size={18} /></button>
                  </div>
                  <div className="grid grid-cols-7 gap-1 mb-1">
                    {['Mo','Tu','We','Th','Fr','Sa','Su'].map(d => <div key={d} className="text-center text-xs font-semibold text-gray-400 py-1">{d}</div>)}
                  </div>
                  <div className="grid grid-cols-7 gap-1">
                  {cells.map((day, i) => {
                      if (!day) return <div key={`e${i}`} />;
                      const ds = `${yr}-${String(mo).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
                      const data = calendarData[ds] || {};
                      const holiday = getHolidayForDate(ds);
                      const isToday = ds === todayStr;
                      const isFuture = ds > todayStr;
                      const isPast = ds < todayStr;
                      let bg = isPast ? 'bg-gray-300 border-gray-400' : 'bg-gray-50 border-gray-100', txt = isPast ? 'text-gray-600' : 'text-gray-500';
                      if (!isFuture) {
                        if (activeTab === 'attendance' && data.total > 0) {
                          if (data.missing === 0) { bg = 'bg-green-50 border-green-300'; txt = 'text-green-800'; }
                          else if (data.missing < data.total) { bg = 'bg-yellow-50 border-yellow-300'; txt = 'text-yellow-800'; }
                          else { bg = 'bg-red-50 border-red-300'; txt = 'text-red-800'; }
                        } else if (activeTab === 'leave' && data.count > 0) { bg = 'bg-blue-50 border-blue-300'; txt = 'text-blue-800'; }
                        else if (activeTab === 'roster') {
                          if (data.rosterCount > 0 && data.rosterCount === data.total) { bg = 'bg-green-50 border-green-300'; txt = 'text-green-800'; }
                          else if (data.rosterCount > 0) { bg = 'bg-yellow-50 border-yellow-300'; txt = 'text-yellow-800'; }
                        } else if (activeTab === 'advances') {
                          if (isFuture) { bg = 'bg-gray-100 border-gray-200 opacity-60'; txt = 'text-gray-400'; }
                          else if (data.count > 0) { bg = 'bg-gray-100 border-gray-800'; txt = 'text-gray-950'; }
                        } else if (activeTab === 'deductions') {
                          if (data.count > 0) { bg = 'bg-red-50 border-red-300'; txt = 'text-red-800'; }
                        }
                      }
                      if (holiday && !data.total && !data.count && !data.rosterCount && !data.advancesTotal && !data.salaryPaidTotal && !data.salaryDueTotal) {
                        bg = 'bg-amber-50 border-amber-300';
                        txt = 'text-amber-800';
                      }
                      return (
                        <div key={ds} title={`Click to view ${ds}`} className={`${bg} border rounded-lg p-1.5 min-h-[58px] cursor-pointer hover:opacity-75 transition ${holiday ? 'ring-1 ring-amber-300' : ''} ${isToday ? 'ring-2 ring-primary-500' : ''}`}
                          onClick={() => drillStaffCalendarDay(ctx, ds, isFuture)}>
                          <div className={`text-xs font-bold ${txt}`}>{day}</div>
                          {holiday && <div className="text-[9px] text-amber-700 font-semibold leading-tight truncate">{holiday.name}</div>}
                          {!isFuture && activeTab === 'attendance' && data.total > 0 && (
                            <div className="mt-0.5">
                              {data.present > 0 && <div className="text-xs text-green-700 leading-tight">✓ {data.present}</div>}
                              {data.onLeave > 0 && (
                                <div>
                                  <div className="text-xs text-blue-600 leading-tight">🏖 {data.onLeave}</div>
                                  {data.onLeaveNames && data.onLeaveNames.slice(0, 2).map((name, i) => (
                                    <div key={i} className="text-[9px] text-blue-500 leading-tight truncate">{name}</div>
                                  ))}
                                  {data.onLeaveNames && data.onLeaveNames.length > 2 && (
                                    <div className="text-[9px] text-blue-400">+{data.onLeaveNames.length - 2}</div>
                                  )}
                                </div>
                              )}
                              {data.missing > 0 && <div className="text-xs text-red-600 leading-tight">✗ {data.missing}</div>}
                              {data.dayOffs && data.dayOffs.map((ev, i) => (
                                <LifecycleBadge key={`off-${i}`} type={ev.type} name={ev.name} filled={ev.filled} size={13} />
                              ))}
                              {data.events && data.events.map((ev, i) => (
                                <LifecycleBadge key={i} type={ev.type} name={ev.name} size={13} />
                              ))}
                            </div>
                          )}
                          {!isFuture && activeTab === 'leave' && data.count > 0 && (
                            <div className="mt-0.5 space-y-0.5">
                              {data.staffNames && data.staffNames.slice(0, 3).map((name, i) => (
                                <div key={i} className="text-[9px] text-blue-700 leading-tight truncate">{name}</div>
                              ))}
                              {data.staffNames && data.staffNames.length > 3 && (
                                <div className="text-[9px] text-blue-600">+{data.staffNames.length - 3} more</div>
                              )}
                            </div>
                          )}
                          {!isFuture && activeTab === 'roster' && data.total > 0 && (
                            <div className="mt-0.5">
                              {data.rosterCount > 0 && <div className="text-xs text-green-700 leading-tight">✓ {data.rosterCount}</div>}
                              {(data.total - data.rosterCount) > 0 && <div className="text-xs text-gray-400 leading-tight">— {data.total - data.rosterCount}</div>}
                            </div>
                          )}
                          {activeTab === 'advances' && isFuture && data.salaryDueTotal > 0 && (
                            <div className="mt-0.5 space-y-0.5">
                              <div className="text-[10px] text-orange-600 font-semibold leading-tight">? Rs.{(data.salaryDueTotal/1000).toFixed(1)}K</div>
                              <div className="text-[9px] text-gray-400 leading-tight truncate">{data.salaryDueStaffNames.map(s => s.split(' ')[0]).join(', ')}</div>
                            </div>
                          )}
                          {!isFuture && activeTab === 'advances' && data.advancesTotal > 0 && (
                            <div className="mt-0.5 space-y-0.5">
                              <div className="text-[10px] text-gray-950 font-semibold leading-tight">Rs.{(data.advancesTotal/1000).toFixed(1)}K</div>
                              <div className="text-[9px] text-gray-400 leading-tight truncate">{data.advancesStaff.map(s => s.split(' ')[0]).join(', ')}</div>
                            </div>
                          )}
                          {!isFuture && activeTab === 'advances' && data.salaryPaidTotal > 0 && (
                            <div className="mt-0.5 space-y-0.5">
                              <div className="text-[10px] text-green-600 font-semibold leading-tight">Rs.{(data.salaryPaidTotal/1000).toFixed(1)}K</div>
                              <div className="text-[9px] text-gray-400 leading-tight truncate">{data.salaryPaidStaff.map(s => s.split(' ')[0]).join(', ')}</div>
                            </div>
                          )}
                          {!isFuture && activeTab === 'deductions' && data.advancesTotal > 0 && (
                            <div className="mt-0.5 space-y-0.5">
                              <div className="text-[10px] text-red-600 font-semibold leading-tight">Rs.{(data.advancesTotal/1000).toFixed(1)}K</div>
                              <div className="text-[9px] text-gray-400 leading-tight truncate">{data.advancesStaff.map(s => s.split(' ')[0]).join(', ')}</div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex flex-wrap gap-4 mt-4 pt-3 border-t text-xs text-gray-400">
                    {activeTab === 'attendance' && (<>
                      <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-green-100 border border-green-300 inline-block"/>All Present</span>
                      <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-yellow-100 border border-yellow-300 inline-block"/>Some Missing</span>
                      <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-red-100 border border-red-300 inline-block"/>All Missing</span>
                      <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-blue-100 border border-blue-300 inline-block"/>On Leave</span>
                      <EventLegend />
                    </>)}
                    {activeTab === 'leave' && (
                      <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-blue-100 border border-blue-300 inline-block"/>Has Leave Entries</span>
                    )}
                    {activeTab === 'roster' && (<>
                      <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-green-100 border border-green-300 inline-block"/>All Rostered</span>
                      <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-yellow-100 border border-yellow-300 inline-block"/>Partial</span>
                    </>)}
                    {activeTab === 'advances' && (
                      <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-blue-100 border border-blue-300 inline-block"/>Advances</span>
                    )}
                    {activeTab === 'deductions' && (
                      <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-red-100 border border-red-300 inline-block"/>Deductions</span>
                    )}
                    <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-amber-50 border border-amber-300 inline-block"/>Public Holiday</span>
                    <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-gray-200 border border-gray-300 inline-block"/>Past No Data</span>
                    <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-gray-50 border border-gray-100 inline-block"/>Future / Upcoming</span>
                    <span className="ml-auto text-gray-300">Click a date to view details</span>
                  </div>
                </div>
              );
}
